import { create } from 'zustand';
import { authAPI } from '../api/auth';
import { tokenManager } from '../utils/tokenManager';
import { securityAuditLogger } from '../utils/securityAuditLogger';
import { useOfflineAuthStore } from './offlineAuthStore';
import type { User, RegisterData, UpdateProfileData, ChangePasswordData } from '../types/user';

interface AuthState {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  lastAuthCheck: number | null; // Timestamp of last successful auth check
  login: (email: string, password: string, rememberMe?: boolean) => Promise<void>;
  register: (data: RegisterData) => Promise<void>;
  logout: () => void;
  checkAuth: () => Promise<void>;
  clearError: () => void;
  updateProfile: (data: UpdateProfileData) => Promise<void>;
  changePassword: (data: ChangePasswordData) => Promise<void>;
  uploadAvatar: (file: File) => Promise<void>;
  deleteAvatar: () => Promise<void>;
}

export const useAuthStore = create<AuthState>((set, get) => ({
  user: null,
  token: localStorage.getItem('jwt_token'),
  isAuthenticated: false,
  isLoading: false,
  error: null,
  lastAuthCheck: null,
  
  login: async (email, password, rememberMe = false) => {
    set({ isLoading: true, error: null });
    try {
      const response = await authAPI.login(email, password, rememberMe);
      
      // Use token manager for secure token storage
      tokenManager.setTokens(
        response.data.token, 
        response.data.refreshToken, 
        response.data.expiresIn
      );
      
      set({ 
        user: response.data.user, 
        token: response.data.token,
        isAuthenticated: true,
        isLoading: false,
        error: null
      });
      
      // Log successful login
      securityAuditLogger.logAuthEvent('login', true, {
        userId: response.data.user.userId,
        email: response.data.user.email,
        rememberMe
      });
      
      // Update offline auth store when login succeeds
      useOfflineAuthStore.getState().checkOfflineAuth();
      
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Login failed';
      
      // Log failed login attempt
      securityAuditLogger.logAuthEvent('login', false, {
        email,
        error: errorMessage,
        timestamp: new Date().toISOString()
      });
      
      set({ 
        isLoading: false, 
        error: errorMessage,
        isAuthenticated: false 
      });
      throw error;
    }
  },
  
  register: async (data) => {
    set({ isLoading: true, error: null });
    try {
      const response = await authAPI.register(data);
      
      // Use token manager for secure token storage
      tokenManager.setTokens(
        response.data.token, 
        response.data.refreshToken, 
        response.data.expiresIn
      );
      
      set({ 
        user: response.data.user, 
        token: response.data.token,
        isAuthenticated: true,
        isLoading: false,
        error: null
      });
      
      // Log successful registration
      securityAuditLogger.logAuthEvent('register', true, {
        userId: response.data.user.userId,
        email: response.data.user.email
      });
      
      // Update offline auth store when registration succeeds
      useOfflineAuthStore.getState().checkOfflineAuth();
      
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Registration failed';
      
      // Log failed registration attempt
      securityAuditLogger.logAuthEvent('register', false, {
        email: data.email,
        error: errorMessage,
        timestamp: new Date().toISOString()
      });
      
      set({ 
        isLoading: false, 
        error: errorMessage,
        isAuthenticated: false 
      });
      throw error;
    }
  },
  
  logout: async () => {
    const currentUser = useAuthStore.getState().user;
    
    try {
      // Call backend logout endpoint to clear Redis session
      await authAPI.logout();
      
      // Log successful logout
      securityAuditLogger.logAuthEvent('logout', true, {
        userId: currentUser?.userId,
        timestamp: new Date().toISOString()
      });
    } catch (error) {
      // Log logout error but continue with local cleanup
      console.warn('Backend logout failed, continuing with local cleanup:', error);
      securityAuditLogger.logAuthEvent('logout', false, {
        userId: currentUser?.userId,
        error: error instanceof Error ? error.message : 'Unknown error',
        timestamp: new Date().toISOString()
      });
    }
    
    // Always clear local state regardless of backend call result
    // Clear tokens using token manager
    tokenManager.clearTokens();
    
    // Clear offline auth state
    useOfflineAuthStore.getState().clearOfflineAuth();
    
    set({ 
      user: null, 
      token: null, 
      isAuthenticated: false,
      error: null 
    });
  },
  
  checkAuth: async () => {
    try {
      // Check if we recently validated (within last 30 seconds)
      const { lastAuthCheck } = get();
      const now = Date.now();
      const CACHE_DURATION = 30 * 1000; // 30 seconds
      
      if (lastAuthCheck && (now - lastAuthCheck) < CACHE_DURATION) {
        console.log('[Auth] Using cached auth check (within 30s)');
        return;
      }
      
      // Use token manager to get valid token
      const token = await tokenManager.getValidToken();
      if (!token) {
        set({ isAuthenticated: false, lastAuthCheck: now });
        return;
      }
      
      const response = await authAPI.getMe();
      
      // Transform backend response to match User type
      // Backend returns { data: { user: {...} } } but type is ApiResponse<User>
      const backendUser = (response.data as any).user || response.data;
      const user: User = {
        userId: backendUser.id || backendUser.userId,
        email: backendUser.email,
        displayName: backendUser.displayName || backendUser.display_name,
        nickname: backendUser.nickname,
        avatarUrl: backendUser.avatarUrl || backendUser.avatar_url,
        birthDate: backendUser.birthDate || backendUser.birth_date,
        zodiacSign: backendUser.zodiacSign || backendUser.zodiac_sign,
        membershipTier: backendUser.membershipTier || backendUser.membership_tier || 'free',
        registrationDate: backendUser.createdAt || backendUser.created_at || backendUser.registrationDate,
        preferences: backendUser.preferences
      };
      
      set({ 
        user, 
        isAuthenticated: true,
        token,
        lastAuthCheck: now
      });
      
      // Log successful auth check
      securityAuditLogger.logSecurityEvent('auth_check_success', {
        userId: user.userId,
        timestamp: new Date().toISOString()
      });
      
      // Update offline auth store when auth check succeeds
      useOfflineAuthStore.getState().checkOfflineAuth();
      
    } catch (error) {
      // Clear tokens and log failed auth check
      tokenManager.clearTokens();
      
      securityAuditLogger.logSecurityEvent('auth_check_failure', {
        error: error instanceof Error ? error.message : 'Unknown error',
        timestamp: new Date().toISOString()
      });
      
      // Clear offline auth state when auth check fails
      useOfflineAuthStore.getState().clearOfflineAuth();
      
      set({ 
        user: null, 
        token: null, 
        isAuthenticated: false 
      });
    }
  },
  
  clearError: () => set({ error: null }),
  
  updateProfile: async (data) => {
    set({ isLoading: true, error: null });
    try {
      const response = await authAPI.updateProfile(data);
      // Backend now returns updated user data in response.data
      const updatedUser = response.data;
      set({ 
        user: updatedUser,
        isLoading: false,
        error: null
      });
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Profile update failed';
      set({ 
        isLoading: false, 
        error: errorMessage
      });
      throw error;
    }
  },
  
  changePassword: async (data) => {
    set({ isLoading: true, error: null });
    const currentUser = useAuthStore.getState().user;
    
    try {
      await authAPI.changePassword(data);
      
      set({ 
        isLoading: false,
        error: null
      });
      
      // Log successful password change
      securityAuditLogger.logAuthEvent('password_change', true, {
        userId: currentUser?.userId,
        timestamp: new Date().toISOString()
      });
      
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Password change failed';
      
      // Log failed password change
      securityAuditLogger.logAuthEvent('password_change', false, {
        userId: currentUser?.userId,
        error: errorMessage,
        timestamp: new Date().toISOString()
      });
      
      set({ 
        isLoading: false, 
        error: errorMessage
      });
      throw error;
    }
  },
  
  uploadAvatar: async (file) => {
    set({ isLoading: true, error: null });
    try {
      const response = await authAPI.uploadAvatar(file);
      set((state) => ({ 
        user: state.user ? { ...state.user, avatarUrl: response.data.avatarUrl } : null,
        isLoading: false,
        error: null
      }));
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Avatar upload failed';
      set({ 
        isLoading: false, 
        error: errorMessage
      });
      throw error;
    }
  },
  
  deleteAvatar: async () => {
    set({ isLoading: true, error: null });
    try {
      await authAPI.deleteAvatar();
      set((state) => ({ 
        user: state.user ? { ...state.user, avatarUrl: undefined } : null,
        isLoading: false,
        error: null
      }));
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Avatar deletion failed';
      set({ 
        isLoading: false, 
        error: errorMessage
      });
      throw error;
    }
  }
}));
